import type { CalendarItem } from '@/types/calendar';
import type { BaseCardProps } from './BaseCalendarCardSvg';
import { formatCompactMinutes } from '@/lib/duration-format';
import { deriveCalendarItemExecutionSummary, deriveCalendarItemIntentText } from '@/lib/calendar-narrative';

type ConvertDistanceFn = (km: number) => { value: number; unit: string };

export interface CalendarCardProps {
  variant: string;
  duration: string;
  workoutType: string;
  title: string;
  distance?: string;
  pace?: string;
  description?: string;
  sparkline?: number[];
}

export type CalendarCardRenderModel =
  | { cardType: 'session'; props: BaseCardProps }
  | { cardType: 'activity'; props: BaseCardProps }
  | { cardType: 'plan_day'; props: BaseCardProps };

/**
 * Derives plan context (WHERE IN THE ARC) from CalendarItem
 * Placeholder implementation - can be enhanced with actual plan data
 */
function derivePlanContext(item: CalendarItem): string | undefined {
  // TODO: Integrate with actual plan data when available
  // For now, return undefined to avoid showing placeholder text
  // This can be populated from plan metadata when that feature is added
  return undefined;
}

/**
 * Maps calendar intent to human-readable workout type
 */
function getWorkoutTypeLabel(intent: string, sport?: string): string {
  // Special handling for race entries
  if (sport === 'race') {
    return 'Race';
  }

  const labels: Record<string, string> = {
    easy: 'Easy',
    steady: 'Steady',
    tempo: 'Tempo',
    intervals: 'Intervals',
    long: 'Long',
    rest: 'Rest',
    // Legacy mappings
    aerobic: 'Easy',
    threshold: 'Tempo',
    vo2: 'Intervals',
    endurance: 'Long',
    recovery: 'Easy',
  };
  return labels[intent] || intent;
}

export function deriveCardVariant(item: CalendarItem): string {
  const isCompleted = item.kind === 'completed';

  // Race entries get special styling
  if (item.sport === 'race') {
    return isCompleted ? 'completed-running' : 'planned-running';
  }

  if (item.sport === 'strength') return 'strength';

  if (item.sport === 'run' || item.sport === 'walk' || item.sport === 'hike' || item.sport === 'other') {
    return isCompleted ? 'completed-running' : 'planned-running';
  }
  if (item.sport === 'ride') {
    return isCompleted ? 'completed-cycling' : 'planned-cycling';
  }
  if (item.sport === 'swim') {
    return isCompleted ? 'completed-swimming' : 'planned-swimming';
  }

  return isCompleted ? 'completed-running' : 'planned-running';
}

export function toCalendarCardProps(item: CalendarItem, convertDistance?: ConvertDistanceFn): CalendarCardProps {
  const variant = deriveCardVariant(item);

  // Format duration
  const duration = formatCompactMinutes(item.durationMin);

  // Get human-readable workout type
  const workoutType = getWorkoutTypeLabel(item.intent, item.sport);

  // Format distance using unit-aware conversion
  const distance = item.distanceKm != null && item.distanceKm > 0
    ? (() => {
        if (convertDistance) {
          const d = convertDistance(item.distanceKm!);
          return `${(Math.round(d.value * 10) / 10)} ${d.unit}`;
        }
        // Fallback: imperial default (miles)
        const mi = Math.round(item.distanceKm! * 0.621371 * 10) / 10;
        return `${mi} mi`;
      })()
    : undefined;

  // Use pace from CalendarItem (extracted from activity)
  const pace = item.pace;

  // Sparkline: only from real API data when available; no mock/simulated values
  const sparkline = undefined;

  // Description: long-form fallback only (deprecated for primary narrative)
  // Do not use for intent or execution - use semantic fields instead
  const description = item.description || undefined;

  return {
    variant,
    duration,
    workoutType,
    title: item.title || workoutType,
    distance,
    pace,
    description,
    sparkline,
  };
}

export function toSessionCardProps(item: CalendarItem, convertDistance?: ConvertDistanceFn): BaseCardProps {
  const variant = deriveCardVariant(item);
  const p = toCalendarCardProps(item, convertDistance);
  const secondaryText =
    p.distance || p.pace ? [p.distance, p.pace].filter(Boolean).join(' · ') : undefined;

  // For merged items (planned + completed), include execution metrics from the paired activity
  // This ensures all activity data (distance, time, TSS, elevation, HR, cadence, pace) is shown
  // When a planned session is paired with a completed activity, show all execution data
  const executionMetrics: BaseCardProps['executionMetrics'] =
    item.isPaired
      ? {
          distance: p.distance,
          duration: p.duration,
          tss: item.load,
          elevation: item.elevation,
          hr: item.hr,
          cadence: item.cadence,
          pace: item.pace, // Pace included in both activity extended and combined
          // Cycling power metrics (only present for cycling activities)
          normalizedPower: item.normalizedPower,
          intensityFactor: item.intensityFactor,
        }
      : undefined;

  return {
    variant,
    topLeft: p.duration,
    topRight: p.workoutType,
    metricsLabel: secondaryText ? 'DISTANCE · AVG PACE' : undefined,
    metricsValue: secondaryText,
    title: p.title,
    description: p.description, // Long-form fallback only
    sparkline: p.sparkline,
    isPlanned: !item.isPaired, // Not planned if paired (it's completed)
    isActivity: item.isPaired, // Show as activity when paired
    // Semantic elevation fields
    planContext: derivePlanContext(item),
    intentText: deriveCalendarItemIntentText(item),
    coachInsight: item.coachNote ?? undefined,
    executionMetrics,
  };
}

function toActivityCardProps(item: CalendarItem, convertDistance?: ConvertDistanceFn): BaseCardProps {
  const variant = deriveCardVariant(item);
  const p = toCalendarCardProps(item, convertDistance);
  const secondaryText =
    p.distance || p.pace ? [p.distance, p.pace].filter(Boolean).join(' · ') : undefined;

  // Execution metrics for completed activities (overview + execution)
  // Includes: distance, time, TSS, elevation (overview) and HR, cadence, pace (execution)
  // For cycling: also includes NP (normalized power) and IF (intensity factor)
  const executionMetrics: BaseCardProps['executionMetrics'] =
    item.kind === 'completed'
      ? {
          distance: p.distance,
          duration: p.duration,
          tss: item.load,
          elevation: item.elevation,
          hr: item.hr,
          cadence: item.cadence,
          pace: item.pace, // Pace included in both activity extended and combined
          // Cycling power metrics (only present for cycling activities)
          normalizedPower: item.normalizedPower,
          intensityFactor: item.intensityFactor,
        }
      : undefined;

  return {
    variant,
    topLeft: p.duration,
    topRight: p.workoutType,
    metricsLabel: secondaryText ? 'DISTANCE · AVG PACE' : undefined,
    metricsValue: secondaryText,
    title: p.title,
    description: p.description,
    sparkline: p.sparkline,
    isActivity: true,
    isPlanned: false,
    planContext: derivePlanContext(item),
    intentText: deriveCalendarItemIntentText(item),
    executionSummary: deriveCalendarItemExecutionSummary(item),
    coachInsight: item.coachNote ?? undefined,
    executionMetrics,
  };
}

function toTrainingDayCardProps(item: CalendarItem, convertDistance?: ConvertDistanceFn): BaseCardProps {
  const variant = deriveCardVariant(item);
  const p = toCalendarCardProps(item, convertDistance);

  return {
    variant,
    topLeft: p.duration,
    topRight: p.workoutType,
    metricsLabel: undefined,
    metricsValue: undefined,
    title: p.title,
    description: p.description, // Long-form fallback only
    sparkline: null,
    titleClampLines: 2,
    descClampLines: 4,
    // Semantic elevation fields
    planContext: derivePlanContext(item),
    intentText: deriveCalendarItemIntentText(item),
    coachInsight: item.coachNote ?? undefined,
  };
}

export function toCalendarCardRenderModel(item: CalendarItem, convertDistance?: ConvertDistanceFn): CalendarCardRenderModel {
  // If paired (planned + completed), always show as activity card with execution metrics
  if (item.isPaired || item.kind === 'completed') {
    return {
      cardType: 'activity',
      props: toActivityCardProps(item, convertDistance),
    };
  }

  // For now, treat all planned items as sessions
  // TODO: Add plan_day detection when that feature is added
  return {
    cardType: 'session',
    props: toSessionCardProps(item, convertDistance),
  };
}

/**
 * Unified card props for a single combined card (plan + execution).
 * Use BaseCalendarCardSvg for both planned and completed – one card that
 * shows intent (planned), execution summary + coach (completed), or both when paired.
 * 
 * When a planned session is paired with a completed activity, treat it as completed
 * to show all execution metrics.
 */
export function toUnifiedCalendarCardProps(item: CalendarItem, convertDistance?: ConvertDistanceFn): BaseCardProps {
  // If paired, always show as activity card with execution metrics
  if (item.isPaired) {
    return toActivityCardProps(item, convertDistance);
  }

  return item.kind === 'completed'
    ? toActivityCardProps(item, convertDistance)
    : toSessionCardProps(item, convertDistance);
}
